import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ESCAPE_PRIORITY, useEscapeAction } from '@peruselab/structure-editor/components';
import { Icon } from '../../components/Icon';

interface SignInPromptProps {
  /** Why signing in is needed, e.g. "Sign in to add this drawing to your paper." */
  readonly message: string;
  readonly onSignIn: () => void;
  /** #889: no account yet -- sign up instead. */
  readonly onSignUp: () => void;
  readonly onClose: () => void;
}

/**
 * #885 (owner decision #632): a gated action stays visible to signed-out visitors and asks them to
 * sign in, rather than disappearing -- the point is to invite them to become signed-in users.
 */
export function SignInPrompt({ message, onSignIn, onSignUp, onClose }: SignInPromptProps) {
  const { t } = useTranslation();
  const signInRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => signInRef.current?.focus(), []);
  useEscapeAction(
    ESCAPE_PRIORITY.overlay,
    () => {
      onClose();
      return true;
    },
    true
  );

  return (
    <div
      className="public-sign-in-backdrop"
      onPointerDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="public-sign-in-prompt" role="dialog" aria-modal="true" aria-labelledby="public-sign-in-title">
        <h2 id="public-sign-in-title">{t('publicPages.prompt.title')}</h2>
        <p>{message}</p>
        <div className="public-sign-in-actions">
          <button ref={signInRef} type="button" className="primary" onClick={onSignIn}>
            {t('publicPages.signIn')}
          </button>
          <button type="button" onClick={onClose}>
            {t('publicPages.prompt.notNow')}
          </button>
        </div>
        <p className="public-sign-in-new">
          {t('publicPages.prompt.noAccount')}{' '}
          <button type="button" className="public-link-button" onClick={onSignUp}>
            {t('publicPages.signUp')}
          </button>
        </p>
        <button
          type="button"
          className="icon-button public-sign-in-close"
          aria-label={t('publicPages.prompt.notNow')}
          title={t('publicPages.prompt.notNow')}
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
    </div>
  );
}
